'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/context/AuthContext'
import { isSuperAdminOrOwner } from '@jira-clone/shared-types'
import { DepartmentsPicker } from '@/features/spaces/components/DepartmentsPicker'
import { getApiErrorMessage } from '@/shared/utils/apiError'
import {
  getSpace,
  updateSpace,
  deleteSpace,
} from '@/features/spaces/api'
import {
  groupServicesByCategory,
} from '@/shared/constants/services'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
} from '@goodday-clone/ui'
import {
  ArrowLeft,
  Settings,
  Compass,
  Kanban,
  Layers,
  GitBranch,
  ListTodo,
  CalendarDays,
  FileText,
  BarChart3,
  Zap,
  Github,
  Save,
  Trash2,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react'
import { CustomFieldsSettingsCard } from './CustomFieldsSettingsCard'

const COLOR_OPTIONS = [
  { label: 'Indigo', value: '#6366f1' },
  { label: 'Blue', value: '#3b82f6' },
  { label: 'Purple', value: '#a855f7' },
  { label: 'Rose', value: '#f43f5e' },
  { label: 'Emerald', value: '#10b981' },
  { label: 'Amber', value: '#f59e0b' },
  { label: 'Cyan', value: '#06b6d4' },
]

export default function SpaceSettingsPage() {
  const params = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { organization, user } = useAuth()
  const canEditDepartments = isSuperAdminOrOwner(user?.role)
  const spaceId = params.id as string

  const [project, setProject] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Form fields
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState('#6366f1')
  const [projectType, setProjectType] = useState('business')
  const [departments, setDepartments] = useState<string[]>([])
  const [status, setStatus] = useState('active')

  const loadProject = useCallback(async () => {
    if (!spaceId) return
    setLoading(true)
    try {
      const space = await getSpace(spaceId)
      setProject(space)
      setName(space.name || '')
      setDescription(space.description || '')
      setColor(space.color || '#6366f1')
      setProjectType(space.projectType || 'business')
      setDepartments(space.departments || [])
      setStatus(space.status || 'active')
    } catch (err: any) {
      console.error('Failed to load project:', err)
      const message = err?.message || 'Failed to load project details.'
      setErrorMessage(message)
    } finally {
      setLoading(false)
    }
  }, [spaceId])

  useEffect(() => {
    loadProject()
  }, [loadProject])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    setSuccessMessage(null)
    setErrorMessage(null)

    try {
      const updated = await updateSpace(spaceId, {
        name: name.trim(),
        description: description.trim() || undefined,
        color,
        type: projectType,
        projectType,
        ...(canEditDepartments ? { departments } : {}),
        status,
      })
      setProject(updated)
      setSuccessMessage('Project settings saved successfully.')
      setTimeout(() => setSuccessMessage(null), 4000)
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'save these project settings'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this project? This will archive the project and its tasks.')) {
      return
    }

    try {
      await deleteSpace(spaceId)
      navigate('/spaces')
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'delete this project'))
    }
  }

  return (
    <div className="flex h-full w-full bg-background overflow-hidden">
      {/* Sidebar Navigation */}
      <aside className="w-64 border-r border-border bg-card flex flex-col shrink-0 hidden lg:block h-full">
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-1">
          <div className="mb-4 pb-4 border-b border-border">
            <Link to="/spaces" className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground mb-4">
              <ArrowLeft className="h-4 w-4" />
              Projects
            </Link>
            <div className="flex items-center gap-3">
              <div
                className="flex h-10 w-10 items-center justify-center rounded-xl text-white font-bold text-sm"
                style={{ backgroundColor: color }}
              >
                {project?.key?.slice(0, 3) || 'PRJ'}
              </div>
              <div>
                <div className="text-xs font-semibold text-muted-foreground uppercase">{project?.key || '...'}</div>
                <h2 className="text-sm font-bold text-foreground line-clamp-1">{project?.name || 'Project'}</h2>
              </div>
            </div>
          </div>

          <nav className="space-y-1">
            <Link
              to={`/spaces/${spaceId}`}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors group"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400 group-hover:scale-110 transition-transform">
                <Compass className="h-3.5 w-3.5" />
              </span>
              <span>Summary</span>
            </Link>
            <Link
              to={`/spaces/${spaceId}/list`}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors group"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400 group-hover:scale-110 transition-transform">
                <ListTodo className="h-3.5 w-3.5" />
              </span>
              <span>List</span>
            </Link>
            <Link
              to={`/spaces/${spaceId}/board`}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors group"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-violet-50 text-violet-600 dark:bg-violet-950/60 dark:text-violet-400 group-hover:scale-110 transition-transform">
                <Kanban className="h-3.5 w-3.5" />
              </span>
              <span>Board</span>
            </Link>
            <Link
              to={`/spaces/${spaceId}/backlog`}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors group"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-amber-50 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400 group-hover:scale-110 transition-transform">
                <Layers className="h-3.5 w-3.5" />
              </span>
              <span>Backlog</span>
            </Link>
            <Link
              to={`/spaces/${spaceId}/calendar`}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors group"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-rose-50 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400 group-hover:scale-110 transition-transform">
                <CalendarDays className="h-3.5 w-3.5" />
              </span>
              <span>Calendar</span>
            </Link>
            <Link
              to={`/spaces/${spaceId}/timeline`}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors group"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-cyan-50 text-cyan-600 dark:bg-cyan-950/60 dark:text-cyan-400 group-hover:scale-110 transition-transform">
                <GitBranch className="h-3.5 w-3.5" />
              </span>
              <span>Timeline</span>
            </Link>
            <Link
              to={`/spaces/${spaceId}/reports`}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors group"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-400 group-hover:scale-110 transition-transform">
                <BarChart3 className="h-3.5 w-3.5" />
              </span>
              <span>Reports</span>
            </Link>
            <Link
              to={`/spaces/${spaceId}/docs`}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors group"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-fuchsia-50 text-fuchsia-600 dark:bg-fuchsia-950/60 dark:text-fuchsia-400 group-hover:scale-110 transition-transform">
                <FileText className="h-3.5 w-3.5" />
              </span>
              <span>Docs</span>
            </Link>
            <Link
              to={`/spaces/${spaceId}/automation`}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors group"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-amber-50 text-amber-500 dark:bg-amber-950/60 dark:text-amber-400 group-hover:scale-110 transition-transform">
                <Zap className="h-3.5 w-3.5" />
              </span>
              <span>Automation</span>
            </Link>
            <Link
              to={`/spaces/${spaceId}/github`}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors group"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 group-hover:scale-110 transition-transform">
                <Github className="h-3.5 w-3.5" />
              </span>
              <span>GitHub</span>
            </Link>
            <Link
              to={`/spaces/${spaceId}/settings`}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100 transition-colors group shadow-2xs"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200 group-hover:scale-110 transition-transform">
                <Settings className="h-3.5 w-3.5" />
              </span>
              <span>Settings</span>
            </Link>
          </nav>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 h-full overflow-y-auto custom-scrollbar">
        <header className="border-b border-border bg-card px-8 py-4 shrink-0">
          <h1 className="text-xl font-bold text-foreground">Project Settings</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Manage space metadata, categorization, and lifecycle</p>
        </header>

        <div className="p-8 pb-32 max-w-4xl space-y-6">
          {successMessage && (
            <div className="p-3.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-300 text-xs font-semibold flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              {successMessage}
            </div>
          )}

          {errorMessage && (
            <div className="p-3.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 dark:bg-rose-950/40 dark:border-rose-800 dark:text-rose-300 text-xs font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {errorMessage}
            </div>
          )}

          {loading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <form onSubmit={handleSave} className="space-y-6">
              {/* General Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-bold">General Details</CardTitle>
                  <CardDescription className="text-xs">Primary identification and descriptive details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="projectName">Project Name</Label>
                      <Input
                        id="projectName"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="projectKey">Project Key</Label>
                      <Input
                        id="projectKey"
                        value={project?.key || ''}
                        disabled
                        className="bg-muted text-muted-foreground cursor-not-allowed font-mono"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="projectDescription">Description</Label>
                    <Textarea
                      id="projectDescription"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      placeholder="Outline project objectives and milestones..."
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>Project Type</Label>
                      <Select value={projectType} onValueChange={setProjectType}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="business">Business Operations</SelectItem>
                          <SelectItem value="service">Service Desk</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1.5">
                      <Label>Status</Label>
                      <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="on_hold">On Hold</SelectItem>
                          <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                    <div className="space-y-1.5 pt-2">
                      <Label>Visible to departments</Label>
                      <DepartmentsPicker value={departments} onChange={setDepartments} disabled={!canEditDepartments} />
                      <p className="text-xs text-muted-foreground">
                        {canEditDepartments
                          ? 'Admins of these departments can see this project. Assigned members always can.'
                          : 'Only a superadmin can change which departments can see this project.'}
                      </p>
                    </div>

                    <div className="space-y-2 pt-2">
                      <Label>Theme Color</Label>
                      <div className="flex flex-wrap items-center gap-2">
                        {COLOR_OPTIONS.map((c) => (
                          <button
                            key={c.value}
                            type="button"
                            onClick={() => setColor(c.value)}
                            className={`h-7 w-7 rounded-full transition-all ${
                              color === c.value ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:opacity-80'
                            }`}
                            style={{ backgroundColor: c.value }}
                            title={c.label}
                          />
                        ))}
                      </div>
                    </div>

                    {project?.services && project.services.length > 0 && (
                      <div className="space-y-3 pt-4 border-t border-border mt-4">
                        <div>
                          <Label className="text-sm font-semibold">Selected Services by Category</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">Services carried over from lead/deal conversion</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                          {groupServicesByCategory(project.services).map(({ category, services: svcs }) => (
                            <div key={category.category} className="rounded-lg border border-border/60 bg-muted/20 p-2.5 space-y-1.5">
                              <div className="flex items-center gap-1.5 text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: category.color }} />
                                <span>{category.category}</span>
                                <span className="text-[10px] text-muted-foreground/70 font-normal">({svcs.length})</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {svcs.map((svc: string) => (
                                  <Badge key={svc} variant="secondary" className={`px-2.5 py-0.5 text-xs ${category.badgeClass}`}>
                                    {svc}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

              {/* Actions row */}
              <div className="flex items-center justify-end gap-3">
                <Button type="submit" disabled={saving || !name.trim()} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5">
                  <Save className="h-4 w-4" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>

              {/* Danger Zone */}
              <Card className="border-rose-200 dark:border-rose-900/60 bg-rose-50/20 dark:bg-rose-950/10">
                <CardHeader>
                  <CardTitle className="text-base font-bold text-rose-600 dark:text-rose-400">Danger Zone</CardTitle>
                  <CardDescription className="text-xs">Irreversible actions that affect project availability</CardDescription>
                </CardHeader>
                <CardContent className="flex items-center justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">Delete this project</h4>
                    <p className="text-xs text-muted-foreground">Once deleted, this project will be removed from your active workspaces.</p>
                  </div>
                  <Button type="button" variant="destructive" onClick={handleDelete} className="gap-1.5 shrink-0">
                    <Trash2 className="h-4 w-4" /> Delete Project
                  </Button>
                </CardContent>
              </Card>
            </form>
          )}

          {!loading && <CustomFieldsSettingsCard projectId={spaceId} />}
        </div>
      </main>
    </div>
  )
}